/* eslint-disable react/destructuring-assignment */
/* eslint-disable prettier/prettier */
import { useCallback, useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { useModals } from 'context';
import { useRequest } from 'hooks';
import {
  deliverTopics,
  enrollToProgram,
  getProgramsList,
  ProgramListResponse,
  Topic,
} from 'services';

import Accordion from 'components/Accordion';
import FilterInput from 'components/FilterInput';
import Loading from 'components/Loading';
import { FeedbackComponentProps } from 'modals/Feedback';

import { DeliverContentModalProps } from './types';

import * as S from './styles';

import { ConfirmComponentProps } from '..';

import { Program } from '.';

const DeliverContent: React.FC<DeliverContentModalProps> = ({
  props,
  isOpen,
  modalKey,
  onClose,
}) => {
  const [programList, setProgramTopics] = useState<ProgramListResponse[]>();
  const [toDeliverTopics, setToDeliverTopics] = useState<number[]>([]);
  const [toEnrollProgram, setToEnrollProgram] = useState<Program>();
  const [searchModule, setSearchModule] = useState<string>('');

  const intl = useIntl();

  const getProgramsRequest = useRequest();
  const getProgramTopicsRequest = useRequest();
  const deliverTopicsRequest = useRequest();
  const enrollToProgramRequest = useRequest();

  const { closeModal, openModal } = useModals();

  const { endUserId, onAfterSubmit, isEnrollment, enrolledProgramId } = props;

  const getPrograms = useCallback(async () => {
    getProgramsRequest.setLoading();
    try {
      if (!endUserId) throw new Error('User ID not found');

      const { data } = await getProgramsList(endUserId);

      setProgramTopics(data);
      getProgramsRequest.setSuccess();
    } catch (e) {
      getProgramsRequest.setError(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleCheck = (
    elementID: number,
    elementArr: number[],
    elementSetState: React.Dispatch<React.SetStateAction<number[]>>,
    checked: boolean,
    onClick?: (programID: number, checked: boolean) => void,
  ) => {
    if (checked) {
      elementSetState([...elementArr, elementID]);
    } else {
      elementSetState((prev: any[]) => prev.filter((el: number) => el !== elementID));
    }

    onClick?.(elementID, checked);
  };

  const handleTopicsDelivery = async () => {
    deliverTopicsRequest.setLoading();
    try {
      await deliverTopics(endUserId, toDeliverTopics);
      await onAfterSubmit();
      closeModal(modalKey);
      deliverTopicsRequest.setSuccess();
      openModal<FeedbackComponentProps>('feedback', {
        props: {
          title: 'DELIVERCONTENT.MODAL.SEND.SUCCESS',
          message: 'DELIVERCONTENT.MODAL.SEND.SUCCESS.MESSAGE',
          status: 'success',
        },
      });
    } catch (e) {
      closeModal(modalKey);

      const fallbackMessage = 'Error while requesting deliver topics';

      deliverTopicsRequest.setError(e, fallbackMessage);

      openModal<FeedbackComponentProps>('feedback', {
        props: {
          title: deliverTopicsRequest.errorMessage() || fallbackMessage,
          message: 'ERROR.TRY',
          status: 'error',
        },
      });
    }
  };

  const handleProgramEnrollment = async () => {
    if (!toEnrollProgram) return;

    enrollToProgramRequest.setLoading();
    try {
      await enrollToProgram(endUserId, toEnrollProgram.id);
      await onAfterSubmit();
      closeModal(modalKey);
      enrollToProgramRequest.setSuccess();
      openModal<FeedbackComponentProps>('feedback', {
        props: {
          title: 'ENDUSER.ACTIVITIES.PROGRAMENROLLMENTFEEDBACK.TITLE',
          message: 'ENDUSER.ACTIVITIES.PROGRAMENROLLMENTFEEDBACK.DESCRIPTION',
          status: 'success',
        },
      });
    } catch (e) {
      closeModal(modalKey);

      const fallbackMessage = 'Error while enrolling to program';
      enrollToProgramRequest.setError(e, fallbackMessage);

      openModal<FeedbackComponentProps>('feedback', {
        props: {
          title: enrollToProgramRequest.errorMessage() || fallbackMessage,
          message: 'ERROR.TRY',
          status: 'error',
        },
      });
    }
  };

  const handleSubmit = () => {
    openModal<ConfirmComponentProps>('confirm', {
      props: {
        message: isEnrollment ? (
          <FormattedMessage
            id="ENDUSER.ACTIVITIES.CONFIRMPROGRAMENROLLMENT"
            values={{
              program: toEnrollProgram?.titleKey,
            }}
          />
        ) : (
          'DELIVERCONTENT.MODAL.TOPICSSELECTED'
        ),
        confirmLabel: 'DELIVERCONTENT.MODAL.SEND',
        cancelLabel: 'DELIVERCONTENT.MODAL.CANCEL',
        hideIcon: true,
        onConfirm: isEnrollment
          ? handleProgramEnrollment
          : handleTopicsDelivery,
      },
    });
    closeModal(modalKey);
  };

  useEffect(() => {
    getPrograms();
  }, [getPrograms]);

  const TopicItemComponent = (el: Topic) => {
    const {id, delivered, titleKey} = el;

    return (
      <S.TopicContent data-testid={`programTopic-${id}`} key={id}>
        {!isEnrollment && (
        <S.CustomCheckbox
          data-testid={`programTopicCheck-${id}`}
          checked={toDeliverTopics.includes(id) || delivered}
          disabled={delivered}
          onChange={(e: { currentTarget: { checked: boolean; }; }) =>
          toggleCheck(
            id,
            toDeliverTopics,
            setToDeliverTopics,
            e.currentTarget.checked,
          )
        }
        />
    )}
        <S.TopicTitle>{titleKey}</S.TopicTitle>
      </S.TopicContent>
    )
  }

  const RenderTopics = (program: ProgramListResponse) => {
    if (isEnrollment) {
      return program.topics.map(el => TopicItemComponent(el));
    }

    return program.topics.map(el => {
      if (el.titleKey.toLowerCase().includes(searchModule.toLowerCase())) {
        return TopicItemComponent(el);
      }

      return null;
    });
  };

  const RenderModalInfo = (program: ProgramListResponse) => {
    const { id, titleKey } = program;
    return (
      <S.ProgramContainer>
        {isEnrollment && (
          <S.CustomCheckbox
            checked={id === enrolledProgramId || id === toEnrollProgram?.id}
            disabled={id === enrolledProgramId}
            onChange={() => {
              if (toEnrollProgram?.id === id)
                return setToEnrollProgram(undefined);
              return setToEnrollProgram(program);
            }}
            data-testid={`programCheck-${id}`}
          />
        )}
        <S.ProgramTitle>{titleKey}</S.ProgramTitle>
      </S.ProgramContainer>
    );
  };

  const RenderPrograms = (programs: ProgramListResponse[] | undefined) => {
    if (!isEnrollment && programs?.length) {
      return programs.map((program: ProgramListResponse) => {
        if (program.topics.find((item: { titleKey: string; }) => item.titleKey.toLowerCase().includes(searchModule.toLowerCase()))) {
          return (
            <S.Program key={program.id} data-testid={`program-${program.id}`}>
              <Accordion titleContent={RenderModalInfo(program)}>
                {RenderTopics(program)}
              </Accordion>
            </S.Program>
          )
        }
        
        return null
      })
    }

    if (isEnrollment && programs?.length) {
      return programs.map((program: ProgramListResponse) => {
        if (program.titleKey.toLowerCase().includes(searchModule.toLowerCase())) {
          return (
            <S.Program key={program.id} data-testid={`program-${program.id}`}>
              <Accordion titleContent={RenderModalInfo(program)}>
                {RenderTopics(program)}
              </Accordion>
            </S.Program>
          )
        }
        
        return null
      })
    }
   
    return null
  }

  return (
    <>
      <Loading
        isLoading={
          getProgramsRequest.isLoading() ||
          getProgramTopicsRequest.isLoading() ||
          deliverTopicsRequest.isLoading() ||
          enrollToProgramRequest.isLoading()
        }
      />
      <S.DeliverContent
        data-testid="deliverContentModal"
        isOpen={isOpen}
        closeModal={() => closeModal(modalKey)}
        onAfterClose={() => {
          if (onClose) {
            onClose();
            setToDeliverTopics([]);
            setProgramTopics([]);
          }
        }}
      >
        <>
          <S.DeliverContentModalTitle data-testid="ProgramModalTitle">
            <FormattedMessage id={!isEnrollment ? "SENDCONTENT.TITLE.MODULE" : "SENDCONTENT.TITLE.PROGRAM"} />
          </S.DeliverContentModalTitle>

          <S.DeliverContentSearchContainer>
            <FilterInput
              data-testid="ProgramModalSearchInput"
              placeholder={intl.formatMessage({ id: !isEnrollment ? "SENDCONTENT.SEARCHFIELD.MODULE" : "SENDCONTENT.SEARCHFIELD.PROGRAM"})}
              value={searchModule}
              onChange={(e: { target: { value: any; }; }) => setSearchModule(e.target.value)}
            />
          </S.DeliverContentSearchContainer>
          {RenderPrograms(programList)}
          <S.CustomButton
            onClick={handleSubmit}
            disabled={
              isEnrollment ? !toEnrollProgram : toDeliverTopics.length < 1
            }
            data-testid="continueButton"
          >
            <FormattedMessage id="DELIVERCONTENT.MODAL.CONTINUE" />
          </S.CustomButton>
        </>
      </S.DeliverContent>
    </>
  );
};

export default DeliverContent;
